import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import './Toast.css';

/*
 * Cabinet-wide transient notifications.
 *
 *   const toast = useToast();
 *   toast.success('Збережено');
 *   toast.error('Не вдалося зберегти');
 *
 * One fixed container (z-index --z-toast, above modals) rendered by
 * <ToastProvider> in App.js. Success messages go to a polite live region,
 * errors to role="alert". Auto-hide: 3s for success, 5s for errors (longer
 * texts, and the user has to act on them). Text only, no "!".
 */

const SUCCESS_MS = 3000;
const ERROR_MS = 5000;
const LEAVE_MS = 180;   // must match .toast--leaving transition in Toast.css
const MAX_TOASTS = 3;

const noop = () => {};
const FALLBACK = { success: noop, error: noop, dismiss: noop };

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Map());

    const remove = useCallback((id) => {
        const t = timers.current.get(id);
        if (t) clearTimeout(t);
        timers.current.delete(id);
        setToasts(list => list.filter(x => x.id !== id));
    }, []);

    const dismiss = useCallback((id) => {
        const t = timers.current.get(id);
        if (t) clearTimeout(t);
        setToasts(list => list.map(x => (x.id === id ? { ...x, leaving: true } : x)));
        timers.current.set(id, setTimeout(() => remove(id), LEAVE_MS));
    }, [remove]);

    const push = useCallback((type, text) => {
        if (!text) return;
        const id = nextId++;
        setToasts(list => {
            // Same text already on screen (double click on "Зберегти") — don't stack copies.
            const rest = list.filter(x => !(x.type === type && x.text === text));
            return [...rest, { id, type, text, leaving: false }].slice(-MAX_TOASTS);
        });
        timers.current.set(id, setTimeout(() => dismiss(id), type === 'error' ? ERROR_MS : SUCCESS_MS));
    }, [dismiss]);

    useEffect(() => {
        const map = timers.current;
        return () => { map.forEach(clearTimeout); map.clear(); };
    }, []);

    const api = useMemo(() => ({
        success: (text) => push('success', text),
        error: (text) => push('error', text),
        dismiss,
    }), [push, dismiss]);

    const renderList = (type) => toasts
        .filter(t => t.type === type)
        .map(t => (
            <div key={t.id} className={`toast toast--${t.type}${t.leaving ? ' toast--leaving' : ''}`}>
                {t.type === 'error'
                    ? <AlertCircle size={16} className="toast__icon" aria-hidden="true" />
                    : <CheckCircle2 size={16} className="toast__icon" aria-hidden="true" />}
                <span className="toast__text">{t.text}</span>
                <button type="button" className="toast__close" onClick={() => dismiss(t.id)} aria-label="Закрити сповіщення">
                    <X size={14} aria-hidden="true" />
                </button>
            </div>
        ));

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div className="toast-container">
                {/* Live regions must exist before content is inserted, so both are always rendered. */}
                <div className="toast-region" role="status" aria-live="polite">{renderList('success')}</div>
                <div className="toast-region" role="alert">{renderList('error')}</div>
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    return useContext(ToastContext) || FALLBACK;
}
